import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    applyAppCompanyContactDraft,
    clearAppCompanyContactDraft,
    createAppCompanyContactDraftPayload,
    createEditAppCompanyContactDraftKey,
    readAppCompanyContactDraft,
    saveAppCompanyContactDraft,
} from './appCompanyContactDraftUtils'

// 상세 조회 실패 시에도 폼 기본값을 명확하게 되돌릴 수 있게 공통 초기값을 사용한다.
const createDefaultParams = () => ({
    companyName: '',
    employeeCount: 0,
    contactPersonName: '',
    contactPersonPosition: '',
    contactEmail: '',
    contactPhone: '',
    content: '',
    regTime: null,
    memo: '',
})

export default function AppCompanyContact(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.companyContactId
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchStatus: 0 }
    const listSearch = location.search || ''
    const draftKey = createEditAppCompanyContactDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [hasDetail, setHasDetail] = useState(false)
    const [saving, setSaving] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [params, setParams] = useState(() => createDefaultParams())
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const initialDraftSnapshotRef = useRef('')

    const getData = useCallback(async () => {
        if (!id) {
            const errorMessage = '필수 파라미터가 없어 기업 도입문의 상세를 불러올 수 없습니다.'
            setParams(createDefaultParams())
            setHasDetail(false)
            setDraftReady(false)
            setDetailErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
            setLoading(false)
            return
        }

        setLoading(true)
        setHasDetail(false)
        setDraftReady(false)
        setDetailErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appCompany/getContactDetail', { companyContactId: id })
            if (data.code === 200) {
                const baseParams = data.data || {}

                if (!baseParams.companyContactId) {
                    setParams(createDefaultParams())
                    setDetailErrorMessage('기업 도입문의 상세 정보를 찾을 수 없습니다.')
                    setLoading(false)
                    return
                }

                const storedDraft = readAppCompanyContactDraft(draftKey)
                const restoredDraft = applyAppCompanyContactDraft({
                    baseParams,
                    draft: storedDraft,
                })

                initialDraftSnapshotRef.current = JSON.stringify(createAppCompanyContactDraftPayload({
                    params: baseParams,
                }))

                setParams(restoredDraft.params)
                setHasDetail(Boolean(baseParams?.companyContactId))
                setDetailErrorMessage('')
                setDraftReady(true)
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '기업 도입문의 상세를 불러오지 못했습니다.'
                setParams(createDefaultParams())
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '기업 도입문의 상세를 불러오지 못했습니다.'
            setParams(createDefaultParams())
            setDetailErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [draftKey, history, id])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        if (!draftReady || !hasDetail) {
            return
        }

        // 도입문의 상세는 목록 query를 건드리지 않고 메모 draft만 sessionStorage에 보존한다.
        const nextDraft = createAppCompanyContactDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppCompanyContactDraft(draftKey)
            return
        }

        saveAppCompanyContactDraft(draftKey, nextDraft)
    }, [draftKey, draftReady, hasDetail, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        // 저장 후에도 운영자가 보던 기업 도입문의 목록 조건으로 그대로 복귀시킨다.
        clearAppCompanyContactDraft(draftKey)
        history.push(createListLinkTarget({
            pathname: '/appCompanyContacts',
            search: listSearch,
            searchText: searchText,
            searchSelect: searchSelect,
            curPage: curPage,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message,
            } : {})
        }))
    }

    const handleUpdateField = (e) => {
        setParams((prev) => ({
            ...prev,
            [e.target.name]: e.target.value
        }))
    }

    const updateData = async (payload) => {
        try {
            setSaving(true)

            const { data } = await API.post('/api/v1/admin/appCompany/updateContact', payload)
            if (data.code === 200) {
                moveToList({ severity: 'success', message: data.message })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('기업 도입문의 메모를 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const nextMemo = String(params.memo || '').trim()

        setParams((prev) => ({
            ...prev,
            memo: nextMemo
        }))

        if (!nextMemo) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('메모를 등록해주세요.')
            return
        }

        updateData({
            companyContactId: id,
            memo: nextMemo
        })
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="기업 도입문의 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" path="/appCompanyContacts" search={listSearch} visible={false} searchText={searchText} searchSelect={searchSelect} curPage={curPage} onBackClick={() => clearAppCompanyContactDraft(draftKey)} backLabel="목록으로"/>
            )}
            sx={{ mt: 2 }}
        />
    )

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                !hasDetail ? (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>기업 도입문의 상세</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
                ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>기업 도입문의 상세</Title>
                    <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} >
                        <Grid container spacing={2} sx={{ marginTop: 2 }}>
                            <Grid item xs={12} sm={12} md={6}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="기업명"
                                        id="companyName"
                                        name="companyName"
                                        value={params.companyName || ''}
                                        size="small"
                                        InputProps={{ readOnly: true}}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={3}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="직원수"
                                        id="employeeCount"
                                        name="employeeCount"
                                        value={params.employeeCount || 0}
                                        size="small"
                                        InputProps={{ readOnly: true}}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={3}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="등록일"
                                        id="regTime"
                                        name="regTime"
                                        value={params.regTime || ''}
                                        size="small"
                                        InputProps={{ readOnly: true}}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={3}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="담당자 이름"
                                        id="contactPersonName"
                                        name="contactPersonName"
                                        value={params.contactPersonName || ''}
                                        size="small"
                                        InputProps={{ readOnly: true}}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={3}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="담당자 직무"
                                        id="contactPersonPosition"
                                        name="contactPersonPosition"
                                        value={params.contactPersonPosition || ''}
                                        size="small"
                                        InputProps={{ readOnly: true}}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={3}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="이메일"
                                        id="contactEmail"
                                        name="contactEmail"
                                        value={params.contactEmail || ''}
                                        size="small"
                                        InputProps={{ readOnly: true}}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={3}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="연락처"
                                        id="contactPhone"
                                        name="contactPhone"
                                        value={params.contactPhone || ''}
                                        size="small"
                                        InputProps={{ readOnly: true}}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="내용"
                                        multiline
                                        rows={7}
                                        name="content"
                                        value={params.content || ''}
                                        size="small"
                                        InputProps={{ readOnly: true}}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="메모"
                                        multiline
                                        rows={7}
                                        name="memo"
                                        value={params.memo || ''}
                                        onChange={handleUpdateField}
                                        disabled={saving}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                        <BottomButtonGroup
                            path="/appCompanyContacts"
                            search={listSearch}
                            visible={true}
                            searchText={searchText}
                            searchSelect={searchSelect}
                            curPage={curPage}
                            submitDisabled={saving}
                            submitLabel={saving ? '저장 중...' : '저장'}
                            onBackClick={() => clearAppCompanyContactDraft(draftKey)}
                        />
                    </Box>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
                )
            )}
        </Grid>
    )
}
